import {
    isPartnerCockpitSubscriptionEnded,
    type PartnerCockpitSubscription,
} from '@houzy/schemas/partner-cockpit/subscription';
import Alert from '@mui/material/Alert';
import AlertTitle from '@mui/material/AlertTitle';
import Button from '@mui/material/Button';
import Link from '@mui/material/Link';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import type { ReactElement } from 'react';
import { Trans, useTranslation } from 'react-i18next';

import { houzyContact } from '../../../lib/houzyContact';
import { formatSwissDate } from '../../../lib/time';
import { subscriptionScheduledCancellationBody } from '../labels';

export type ScheduledCancellationPanelProps = {
    subscription: PartnerCockpitSubscription;
    isSubmitting: boolean;
    errorMessage?: string;
    onRevoke: () => void;
};

export function ScheduledCancellationPanel({
    subscription,
    isSubmitting,
    errorMessage,
    onRevoke,
}: ScheduledCancellationPanelProps): ReactElement | null {
    const { t } = useTranslation();
    const { cancelEffectiveAt, endsCoveredPeriod, canRevokeCancellation } = subscription;

    if (!cancelEffectiveAt || isPartnerCockpitSubscriptionEnded(subscription)) {
        return null;
    }
    const date = formatSwissDate(new Date(cancelEffectiveAt));

    return (
        <Alert severity="warning">
            <AlertTitle>{t('SETTINGS.SUBSCRIPTION.SCHEDULED_CANCELLATION.TITLE', { date })}</AlertTitle>
            <Stack spacing={1.5} sx={{ alignItems: 'flex-start' }}>
                <Typography variant="body2">
                    {subscriptionScheduledCancellationBody(t, endsCoveredPeriod, date)}
                </Typography>

                {canRevokeCancellation ? (
                    <Button variant="outlined" color="inherit" disabled={isSubmitting} onClick={onRevoke}>
                        {t('SETTINGS.SUBSCRIPTION.SCHEDULED_CANCELLATION.REVOKE')}
                    </Button>
                ) : (
                    <Typography variant="body2">
                        <Trans
                            i18nKey="SETTINGS.SUBSCRIPTION.SCHEDULED_CANCELLATION.TOO_LATE"
                            components={{
                                supportLink: <Link href={`mailto:${houzyContact.supportEmail}`} underline="hover" />,
                            }}
                        />
                    </Typography>
                )}

                {errorMessage && (
                    <Typography variant="body2" color="error">
                        {errorMessage}
                    </Typography>
                )}
            </Stack>
        </Alert>
    );
}
